import React, { useState, useCallback } from 'react'
import { Modal, Button, Alert, Form, Spinner } from 'react-bootstrap'
import { useDropzone } from 'react-dropzone'
import * as FaIcons from 'react-icons/fa'
import PropTypes from 'prop-types'

// Modal de carga superpuesto
const LoadingModal = ({ show, message }) => (
    <Modal
        show={show}
        backdrop="static"
        keyboard={false}
        centered
        style={{ zIndex: 1060 }}>
        <Modal.Body className="text-center py-4">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <h5 className="mt-3 mb-2">{message || 'Procesando...'}</h5>
            <p className="text-muted mb-0">Por favor espere</p>
        </Modal.Body>
    </Modal>
)

const BulkUploadModal = ({ show, onHide, onDownloadPlantilla, onUploadFile, loading, downloadingPlantilla }) => {
    const [selectedFile, setSelectedFile] = useState(null)
    const [uploadError, setUploadError] = useState(null)
    const [isUploading, setIsUploading] = useState(false)

    // Configuración del dropzone
    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        setUploadError(null)
        
        if (rejectedFiles && rejectedFiles.length > 0) {
            const rejection = rejectedFiles[0]
            if (rejection.errors && rejection.errors.length > 0) {
                const errorCode = rejection.errors[0].code
                if (errorCode === 'file-invalid-type') {
                    setUploadError('El archivo debe ser un Excel (.xlsx o .xls)')
                } else if (errorCode === 'file-too-large') {
                    setUploadError('El archivo es demasiado grande. Máximo 10MB')
                } else {
                    setUploadError('Error al cargar el archivo')
                }
            }
            return
        }

        if (acceptedFiles && acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0])
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls']
        },
        maxFiles: 1,
        maxSize: 10485760, // 10MB
        disabled: loading || isUploading
    })

    const handleRemoveFile = () => {
        setSelectedFile(null)
        setUploadError(null)
    }

    const handleUpload = async () => {
        if (selectedFile && onUploadFile) {
            setIsUploading(true)
            try {
                await onUploadFile(selectedFile)
            } finally {
                setIsUploading(false)
            }
        }
    }

    const handleClose = () => {
        if (!loading && !isUploading) {
            setSelectedFile(null)
            setUploadError(null)
            setIsUploading(false)
            onHide()
        }
    }

    // Limpiar archivo cuando se cierra el modal
    React.useEffect(() => {
        if (!show) {
            setSelectedFile(null)
            setUploadError(null)
            setIsUploading(false)
        }
    }, [show])

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            backdrop={loading || isUploading ? 'static' : true}
            keyboard={!loading && !isUploading}>
            <Modal.Header closeButton={!loading && !isUploading}>
                <Modal.Title>
                    <FaIcons.FaFileExcel className="me-2" style={{ color: '#198754' }} />
                    Carga Masiva de Rutas
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Instrucciones */}
                <Alert variant="info" className="mb-4">
                    <FaIcons.FaInfoCircle className="me-2" />
                    <strong>Instrucciones:</strong>
                    <ol className="mb-0 mt-2">
                        <li>Descarga la plantilla Excel haciendo clic en el botón de abajo</li>
                        <li>Completa la información de las rutas en el archivo</li>
                        <li>Verifica que la información de los campos: 
                            <br/>TMS, Usuario_Spread, 
                            PPU_Vehiculo, Tipo_Precio, Cliente, Centro_Despacho, Servicio_Spread, 
                            Tamano_Spread, Tamano_Cliente, Local_Cliente.<br/>
                            Existan en la pestaña "Lista_Validacion", Sino fallara la carga.</li>
                        <li>Sube el archivo completado usando el área de carga</li>
                    </ol>
                </Alert>

                {/* Botón de descarga de plantilla */}
                <div className="mb-4 text-center">
                    <Button
                        variant="outline-primary"
                        size="lg"
                        onClick={onDownloadPlantilla}
                        disabled={loading || downloadingPlantilla}
                        style={{ minWidth: '250px' }}>
                        {downloadingPlantilla ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Descargando...</span>
                                </div>
                                Descargando...
                            </>
                        ) : (
                            <>
                                <FaIcons.FaDownload className="me-2" />
                                Descargar Plantilla Excel
                            </>
                        )}
                    </Button>
                </div>

                <div className="border-top pt-4">
                    <Form.Label className="fw-bold mb-3">
                        <FaIcons.FaUpload className="me-2" />
                        Subir Archivo Excel
                    </Form.Label>

                    {/* Dropzone */}
                    <div
                        {...getRootProps()}
                        style={{
                            border: `2px dashed ${isDragActive ? '#0d6efd' : selectedFile ? '#198754' : '#dee2e6'}`,
                            borderRadius: '8px',
                            padding: '40px 20px',
                            textAlign: 'center',
                            backgroundColor: isDragActive ? '#e7f3ff' : selectedFile ? '#d1e7dd' : '#f8f9fa',
                            cursor: (loading || isUploading) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            opacity: (loading || isUploading) ? 0.6 : 1
                        }}>
                        <input {...getInputProps()} />
                        
                        {selectedFile ? (
                            <div>
                                <FaIcons.FaCheckCircle 
                                    style={{ 
                                        fontSize: '3rem', 
                                        color: '#198754',
                                        marginBottom: '15px'
                                    }} 
                                />
                                <h5 className="mb-2">Archivo Seleccionado</h5>
                                <p className="text-muted mb-3">
                                    <FaIcons.FaFileExcel className="me-2" style={{ color: '#198754' }} />
                                    {selectedFile.name}
                                </p>
                                <p className="text-muted small mb-2">
                                    Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                                </p>
                                {!loading && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleRemoveFile()
                                        }}>
                                        <FaIcons.FaTrash className="me-1" />
                                        Quitar archivo
                                    </Button>
                                )}
                            </div>
                        ) : isDragActive ? (
                            <div>
                                <FaIcons.FaCloudUploadAlt 
                                    style={{ 
                                        fontSize: '3rem', 
                                        color: '#0d6efd',
                                        marginBottom: '15px'
                                    }} 
                                />
                                <h5 className="mb-2">Suelta el archivo aquí</h5>
                            </div>
                        ) : (
                            <div>
                                <FaIcons.FaCloudUploadAlt 
                                    style={{ 
                                        fontSize: '3rem', 
                                        color: '#6c757d',
                                        marginBottom: '15px'
                                    }} 
                                />
                                <h5 className="mb-2">Arrastra y suelta tu archivo Excel aquí</h5>
                                <p className="text-muted mb-2">o</p>
                                <Button variant="outline-secondary" size="sm" disabled={loading}>
                                    <FaIcons.FaFolderOpen className="me-2" />
                                    Buscar archivo
                                </Button>
                                <p className="text-muted small mt-3 mb-0">
                                    Formatos aceptados: .xlsx, .xls (Máximo 10MB)
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Errores */}
                    {uploadError && (
                        <Alert variant="danger" className="mt-3 mb-0">
                            <FaIcons.FaExclamationTriangle className="me-2" />
                            {uploadError}
                        </Alert>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button 
                    variant="secondary" 
                    onClick={handleClose}
                    disabled={loading || isUploading}>
                    Cancelar
                </Button>
                <Button 
                    variant="success" 
                    onClick={handleUpload}
                    disabled={!selectedFile || loading || isUploading}>
                    <FaIcons.FaUpload className="me-2" />
                    Cargar Rutas
                </Button>
            </Modal.Footer>
            
            {/* Modal de carga superpuesto */}
            <LoadingModal 
                show={isUploading} 
                message="Validando archivo Excel..." 
            />
        </Modal>
    )
}

BulkUploadModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onDownloadPlantilla: PropTypes.func.isRequired,
    onUploadFile: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    downloadingPlantilla: PropTypes.bool
}

BulkUploadModal.defaultProps = {
    loading: false,
    downloadingPlantilla: false
}

export default BulkUploadModal
